import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestIdleCallback, requestYieldCallback } from "@theme/utilities";

/**
 * Global cache shared across all variant picker instances
 * This allows cache to persist when switching between products in combined listings
 *
 * HYBRID APPROACH:
 * - Cache preloaded variants for instant switching (no network delay)
 * - Fetch lightweight JSON for uncached variants (faster than full HTML)
 * - Fallback to full HTML when variant ID not available
 */
const globalVariantsCache = new Map();
const globalMediaCache = new Map();

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #lastProductId;

  connectedCallback() {
    super.connectedCallback();

    // Store initial product ID
    this.#lastProductId = this.dataset.productId;

    // HYBRID: Load and cache preloaded variants for instant switching
    this.#loadVariantsCache();

    this.addEventListener("change", this.variantChanged.bind(this));

     if(this.closest("quick-add-dialog")){
     this.quickAddDrawerContent = document.getElementById("quick-add-drawer-content");
     }else{
      this.quickAddDrawerContent = this.closest(".product-information__details");
     } 
     //debugger;
    this.inventoryLevels = {};
    if(this.quickAddDrawerContent){
    this.addToCartButton = this.quickAddDrawerContent.querySelector(".add-to-cart-button");
    this.addToCartButtonText = this.addToCartButton.querySelector(".add-to-cart-text .add-to-cart-text__content");
    this.inventoryStatus = this.quickAddDrawerContent.querySelector(".product-inventory__status");
    var variantId = this.quickAddDrawerContent.querySelector("[name='id']").value;
    this.productID = this.quickAddDrawerContent.querySelector("[name='product-id']").value;
    this.stickyAddToCartButton = document.querySelector(".sticky-add-to-cart[data-product-id='"+this.productID+"'] .sticky-add-to-cart__button");
    if(this.stickyAddToCartButton){
    this.stickyAddToCartButtonText = this.stickyAddToCartButton.querySelector(".btn__text");
    }
      this._handleInventoryCheck(variantId, this.productID);
    }

    if(this.closest(".product-information__details")){
      this.variantBuilderIds = {};
      if(typeof glow != "undefined"){
        glow.initialize({ retailerId: "RT-4ED7", themeColor: "#FE5000" });
        this.initializeBuilderData();
        this.updateGlowButton(variantId);
      } 
      this.variantYoutubeUrls = {};
      this.initializeYouTubeData();
    }

  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Abort any pending fetch requests to prevent memory leaks
    this.#abortController?.abort();
    this.removeEventListener("change", this.variantChanged.bind(this));
  }

  updatedCallback() {
    super.updatedCallback?.();

    // When product changes (combine listing), re-load cache for new product
    const currentProductId = this.dataset.productId;
    if (currentProductId !== this.#lastProductId) {
      this.#lastProductId = currentProductId;
      // HYBRID: Re-load cache for new product
      this.#loadVariantsCache();
    }
  }

  updateGlowButton(variantId) {
    console.log("Updating glow button for variant:", variantId);
    console.log("Available builder IDs:", this.variantBuilderIds);

    if (!this.variantBuilderIds || !variantId) {
      console.error("Missing builder IDs or variant ID");
      return;
    }

    const productId = this.variantBuilderIds[variantId];
    console.log("Found product ID:", productId);

    if (!productId) {
      console.error("No product ID found for variant", variantId);
      const existingButton = this.quickAddDrawerContent.querySelector(".glow-tech-preview-btn");
      if (existingButton) {
        existingButton.remove();
      }
      return;
    }

    let glowButton = this.quickAddDrawerContent.querySelector(".glow-tech-preview-btn");
    const buyButtonsContainer = this.quickAddDrawerContent.querySelector(
      ".buy-buttons-block"
    );

    if (!glowButton && buyButtonsContainer) {
      glowButton = document.createElement("button");
      glowButton.type = "button";
      glowButton.className = "glow-tech-btn glow-tech-preview-btn btn btn--primary";
      glowButton.textContent = "Watch now in 3D!";
      buyButtonsContainer.parentNode.insertBefore(
        glowButton,
        buyButtonsContainer.nextSibling
      );
    } else if (!glowButton) {
      console.error("Buy buttons container not found");
      return;
    }

    console.log("Setting up glow button with productId:", productId);

    const newGlowButton = glowButton.cloneNode(true);
    glowButton.parentNode.replaceChild(newGlowButton, glowButton);

    newGlowButton.addEventListener("click", () => {
      console.log("Glow button clicked. Calling previewProduct with:", {
        brandId: "182",
        productId: productId,
      });

      glow.previewProduct({
        brandId: "182",
        productId: productId,
      });
    });
  }

  initializeYouTubeData() {
    const youtubeDataElement = document.getElementById("variant-youtube-data");
    if (!youtubeDataElement) return;
    const variantVideosData = youtubeDataElement.dataset.variantVideos;
    if (!variantVideosData) return;
    this.variantYoutubeUrls = JSON.parse(variantVideosData);
  }

  initializeBuilderData() {
    const builderDataElement = document.getElementById("variant-builder-data");
    console.log(
      "Builder Data Element:",
      builderDataElement?.dataset.variantBuilders
    );

    if (!builderDataElement) {
      console.error("No builder data element found");
      return;
    }

    const variantBuildersData = builderDataElement.dataset.variantBuilders;
    console.log("Raw Builder Data:", variantBuildersData);

    if (!variantBuildersData) {
      console.error("No variant builders data found");
      return;
    }

    try {
      this.variantBuilderIds = JSON.parse(variantBuildersData);
      console.log("Parsed Builder IDs mapping:", this.variantBuilderIds);
    } catch (error) {
      console.error("Error parsing builder data:", error);
    }
  }

  updateYouTubeVideo(variantId) {
    const videoContainer = document.querySelector(".meta-variant-video-wrapper");
    if (!this.variantYoutubeUrls){ 
      if(videoContainer){
        videoContainer.classList.remove("visible-meta-video");
      }
      return;
    }
    const videoId = this.variantYoutubeUrls[variantId];
   
    if (!videoId) return;
    const youtubeEmbedUrl = `https://www.youtube.com/embed/${videoId}`;
    if (
      !youtubeEmbedUrl.startsWith("https://www.youtube.com/embed/") ||
      !videoContainer
    )
      return;

    let iframe = videoContainer.querySelector("iframe");
    if (iframe) iframe.remove();

    iframe = document.createElement("iframe");
    iframe.src = youtubeEmbedUrl;
    iframe.classList.add("js-youtube");
    iframe.setAttribute(
      "allow",
      "autoplay; encrypted-media; picture-in-picture"
    );
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("playsinline", "");
    iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
    videoContainer.querySelector(".deferred-media__video-wrap").innerHTML = "";
    videoContainer.querySelector(".deferred-media__video-wrap").appendChild(iframe);
    videoContainer.classList.add("visible-meta-video");

  }

  // _handleSwatchAvailability() {
  //   var _this = this;
  //    var productID = this.quickAddDrawerContent.querySelector("[name='product-id']").value;
  //    var url = "https://multiloca.info/api/get-loc-inventory-stock?shopdomain=elite-fireworks-national.myshopify.com&product_id=" + productID;
  //    var options = {
  //   method: 'GET',
  //   headers: {
  //     'Content-Type': 'application/json'
  //   }
  // }

  //   fetch(url, options)
  //   .then(response => response.json())
  //   .then(json => {    
  //    var inventoryLevels = json.inventoryLevels; 

    
  //   var radios = this.querySelectorAll(".variant-option__button-label input");
  //   radios.forEach((radio) => {
  //     var variantID = radio.getAttribute("data-variant-id");
  //     var selectedStore =
  //       document.cookie
  //         .split("; ")
  //         .find((row) => row.startsWith("userSelectedLocation="))
  //         ?.split("=")[1] || "";

  //     if(variantID != null && variantID != ""){
  //      var variantInventoryLevel = inventoryLevels[variantID];
  //      console.log(variantInventoryLevel);
  //      var inventoryLevelLocations = variantInventoryLevel.locations;

  //       if(inventoryLevelLocations[0].location_name.indexOf("Texas") != -1){
  //       var texasAvailability = inventoryLevelLocations[0].inventory_data[0].available;
  //       var newHampshireAvailability = inventoryLevelLocations[1].inventory_data[0].available;
  //     }else{
  //       var texasAvailability = inventoryLevelLocations[1].inventory_data[0].available;
  //       var newHampshireAvailability = inventoryLevelLocations[0].inventory_data[0].available;
  //     }

  //     var itemInStock = false;

  //     if(selectedStore === "Texas" && texasAvailability > 0){
  //       itemInStock = true;
  //     }else if(selectedStore === "New Hampshire" && newHampshireAvailability > 0){
  //       itemInStock = true;
  //     }

  //    if(!itemInStock){
  //       var strikethroughVariantDiv = document.createElement("div");
  //       strikethroughVariantDiv.classList.add("strikethrough-variant");
  //       radio.closest(".variant-option__button-label").appendChild(strikethroughVariantDiv);
  //       radio.setAttribute("data-option-available",false);
  //     }

  //     }
  //   })

  //   });
  // }


    _handleSwatchAvailability(inventoryLevels) {
    var _this = this;
    var radios = this.querySelectorAll(".variant-option__button-label input");
    var options = this.querySelectorAll(".variant-option__select option");
    radios.forEach((radio) => {
      var variantID = radio.getAttribute("data-variant-id");
      var selectedStore =
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("userSelectedLocation="))
          ?.split("=")[1] || "";

      if(variantID != null && variantID != ""){
       var variantInventoryLevel = inventoryLevels[variantID];
       console.log(variantInventoryLevel);
       var inventoryLevelLocations = variantInventoryLevel.locations;

        if(inventoryLevelLocations[0].location_name.indexOf("Texas") != -1){
        var texasAvailability = inventoryLevelLocations[0].inventory_data[0].available;
        var newHampshireAvailability = inventoryLevelLocations[1].inventory_data[0].available;
      }else{
        var texasAvailability = inventoryLevelLocations[1].inventory_data[0].available;
        var newHampshireAvailability = inventoryLevelLocations[0].inventory_data[0].available;
      }

      var itemInStock = false;

      if(selectedStore === "Texas" && texasAvailability > 0){
        itemInStock = true;
      }else if(selectedStore === "New Hampshire" && newHampshireAvailability > 0){
        itemInStock = true;
      }

      if(!itemInStock){
        var strikethroughVariantDiv = document.createElement("div");
        strikethroughVariantDiv.classList.add("strikethrough-variant");
        radio.closest(".variant-option__button-label").appendChild(strikethroughVariantDiv);
        radio.setAttribute("data-option-available",false);
      }

      }
    })

    options.forEach((option) => {
      var variantID = option.getAttribute("data-variant-id");
      var selectedStore =
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("userSelectedLocation="))
          ?.split("=")[1] || "";

      if(variantID != null && variantID != ""){
       var variantInventoryLevel = inventoryLevels[variantID];
       console.log(variantInventoryLevel);
       var inventoryLevelLocations = variantInventoryLevel.locations;

        if(inventoryLevelLocations[0].location_name.indexOf("Texas") != -1){
        var texasAvailability = inventoryLevelLocations[0].inventory_data[0].available;
        var newHampshireAvailability = inventoryLevelLocations[1].inventory_data[0].available;
      }else{
        var texasAvailability = inventoryLevelLocations[1].inventory_data[0].available;
        var newHampshireAvailability = inventoryLevelLocations[0].inventory_data[0].available;
      }

      var itemInStock = false;

      if(selectedStore === "Texas" && texasAvailability > 0){
        itemInStock = true;
      }else if(selectedStore === "New Hampshire" && newHampshireAvailability > 0){
        itemInStock = true;
      }

      if(!itemInStock){
        option.setAttribute("disabled",true);
      }

      }
    })

  }


  updateInventoryStatus(inventoryLevelLocations){
    const selectedStore =
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("userSelectedLocation="))
      ?.split("=")[1] || "";

    var _this = this;
    if(inventoryLevelLocations[0].location_name.indexOf("Texas") != -1){
        var texasAvailability = inventoryLevelLocations[0].inventory_data[0].available;
        var newHampshireAvailability = inventoryLevelLocations[1].inventory_data[0].available;
      }else{
        var texasAvailability = inventoryLevelLocations[1].inventory_data[0].available;
        var newHampshireAvailability = inventoryLevelLocations[0].inventory_data[0].available;
      }

      var itemInStock = false;

      if(selectedStore === "Texas" && texasAvailability > 0){
        itemInStock = true;
      }else if(selectedStore === "New Hampshire" && newHampshireAvailability > 0){
        itemInStock = true;
      }

      if (itemInStock) {
        // Item is in stock, now check for Texas Unit restriction
     //   if (selectedStore === "Texas") {
          var inputElements = this.quickAddDrawerContent.querySelectorAll(
              'input[value$="Unit"], input[value$="Sleeve Kit™"]'
            );
          let isUnitVariant = false;

          inputElements.forEach(function (inputElement) {
            const valueParts = inputElement.value.split(" ");
            const lastWord = valueParts[valueParts.length - 1];
            const beforeLastWord = valueParts.length > 2 ? valueParts[valueParts.length - 2]: "";

              if ((lastWord === "Unit" || (beforeLastWord == "Sleeve" && lastWord == "Kit™")) && inputElement.checked) {
              isUnitVariant = true;
              console.log("Unit restriction applies");


              // Update original button for in-store only
              if (_this.addToCartButton) {
                  _this.addToCartButton.classList.add("btn--secondary");
                  _this.addToCartButton.setAttribute("disabled","disabled"); 
                  _this.addToCartButtonText.innerHTML = "Available in-store only";
              }

                if(_this.stickyAddToCartButton){ 
                  _this.stickyAddToCartButton.classList.add("btn--secondary");
                  _this.stickyAddToCartButton.setAttribute("disabled","disabled"); 
                  _this.stickyAddToCartButtonText.innerHTML = "Available in-store only";
                }
               if(this.inventoryStatus){
                this.inventoryStatus.setAttribute("hidden","");
              }
            }
          });

          var optionElements = this.quickAddDrawerContent.querySelectorAll(
              'option[value$="Unit"], option[value$="Sleeve Kit™"]'
            );


          optionElements.forEach(function (optionElement) {
            const valueParts = optionElement.value.split(" ");
            const lastWord = valueParts[valueParts.length - 1];
            const beforeLastWord = valueParts.length > 2 ? valueParts[valueParts.length - 2]: "";

              if ((lastWord === "Unit" || (beforeLastWord == "Sleeve" && lastWord == "Kit™")) && optionElement.selected) {
              isUnitVariant = true;
              console.log("Unit restriction applies");


              // Update original button for in-store only
              if (_this.addToCartButton) {
                  _this.addToCartButton.classList.add("btn--secondary");
                  _this.addToCartButton.setAttribute("disabled","disabled"); 
                  _this.addToCartButtonText.innerHTML = "Available in-store only";
              }
               if(_this.stickyAddToCartButton){ 
                 _this.stickyAddToCartButton.classList.add("btn--secondary");
                 _this.stickyAddToCartButton.setAttribute("disabled","disabled"); 
                 _this.stickyAddToCartButtonText.innerHTML = "Available in-store only";
               }
              if(_this.inventoryStatus){
                _this.inventoryStatus.setAttribute("hidden","");
              }
            }
          });


          // If not a Unit variant, show normal in-stock state
          if (!isUnitVariant) {
            if (_this.addToCartButton) {
              _this.addToCartButton.classList.remove("btn--secondary");
              _this.addToCartButton.removeAttribute("disabled");
              _this.addToCartButtonText.innerHTML = "Add to Cart";
            }
              if(_this.stickyAddToCartButton){ 
                _this.stickyAddToCartButton.classList.remove("btn--secondary");
              _this.stickyAddToCartButton.removeAttribute("disabled");
              _this.stickyAddToCartButtonText.innerHTML = "Add to Cart";
              }
            if(_this.inventoryStatus){
              _this.inventoryStatus.removeAttribute("hidden");
            }
          }
        // } else {
        //   if (_this.addToCartButton) {
        //      _this.addToCartButton.classList.remove("button--ternary");
        //       _this.addToCartButton.removeAttribute("disabled");
        //       _this.addToCartButtonText.innerHTML = "Add to Cart";
        //   }
        // }
      } else {
        // Out of stock logic remains the same for all cases
        _this.addToCartButton.classList.add("btn--secondary");
        _this.addToCartButton.setAttribute("disabled","disabled"); 
        _this.addToCartButtonText.innerHTML = "Out of Stock";

          if(_this.stickyAddToCartButton){ 
            _this.stickyAddToCartButton.classList.add("btn--secondary");
            _this.stickyAddToCartButton.setAttribute("disabled","disabled"); 
            _this.stickyAddToCartButtonText.innerHTML = "Out of Stock";
          }
        if(_this.inventoryStatus){
          _this.inventoryStatus.setAttribute("hidden","");
        }
      }
  }
  _handleInventoryCheck(variantID,productID) {
    const selectedStore =
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("userSelectedLocation="))
          ?.split("=")[1] || "";

      if(selectedStore == ""){
        this.addToCartButton.setAttribute("type","button");
        this.addToCartButton.removeAttribute("disabled");
        this.addToCartButton.classList.add("btn--state-location");
        this.addToCartButtonText.innerHTML = "Check availability near you";
        this.addToCartButton.addEventListener("click", (event) => {
            event.preventDefault();
            showPositionProductPage();
            this.addToCartButton.setAttribute("type","submit");
          },{ once: true });

         if(this.stickyAddToCartButton){ 
        this.stickyAddToCartButton.setAttribute("type","button");
        this.stickyAddToCartButton.removeAttribute("disabled");
        this.stickyAddToCartButton.classList.add("btn--state-location");
        this.stickyAddToCartButtonText.innerHTML = "Check availability near you";
        this.stickyAddToCartButton.addEventListener("click", (event) => {
            event.preventDefault();
            showPositionProductPage();
              this.addToCartButton.setAttribute("type","submit");
          },{ once: true });
        }
          if(this.inventoryStatus){
                this.inventoryStatus.setAttribute("hidden","");
              }

              if(document.querySelector('#LocationModalComponent')){
                  setTimeout(() => {
                  document.querySelector('#LocationModalComponent').showDialog(); 
                  },5000);
              }
        return;
         
        }else{
        
          this.addToCartButton.classList.add("btn--secondary");
          this.addToCartButton.setAttribute("disabled","disabled");
          this.addToCartButtonText.innerHTML = "Loading...";

            if(this.stickyAddToCartButton){ 
          this.stickyAddToCartButton.classList.add("btn--secondary");
          this.stickyAddToCartButton.setAttribute("disabled","disabled");
          this.stickyAddToCartButtonText.innerHTML = "Loading...";
            }
        }


      if(Object.keys(this.inventoryLevels).length> 0){
          this._handleSwatchAvailability(this.inventoryLevels); 
          this.updateInventoryStatus(this.inventoryLevels[variantID].locations);
      }else{
        var url = "https://multiloca.info/api/get-loc-inventory-stock?shopdomain=elite-fireworks-national.myshopify.com&product_id=" + productID;
          var options = {
          method: 'GET'
        }

        fetch(url, options)
        .then((response) => {
            if (!response.ok && response.status === 429) {
              setTimeout(() => {
                  this._handleInventoryCheck(variantID,productID);
              },3000);
            }else{
            return response.json();
            }
        })
        .then(json => {    
            this._handleSwatchAvailability(json.inventoryLevels); 
            var variantInventoryLevel = json.inventoryLevels[variantID];
            var inventoryLevelLocations = variantInventoryLevel.locations;
            this.inventoryLevels = json.inventoryLevels;
            this.updateInventoryStatus(inventoryLevelLocations);
        });
    }
  }


  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // HYBRID APPROACH: Cache first, then JSON fetch, then full HTML fallback
    const variantId = selectedOption.dataset.variantId;
    const cachedVariant = this.#getCachedVariant();

    if (cachedVariant) {
      // ⚡ CACHE HIT: Instant update with no network delay
      this.#updateCriticalUI(cachedVariant, true);

      // Detect if product changed (for combine listings in quick-add drawer, product cards, etc.)
      const newProductUrl = selectedOption.dataset.connectedProductUrl;
      const currentProductUrl = this.dataset.productUrl?.split("?")[0];
      const hasProductChanged = newProductUrl && currentProductUrl !== newProductUrl;

      // Dispatch event immediately for other components
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(cachedVariant, this.selectedOptionId, {
            html: null, // No HTML yet
            productId: this.dataset.productId ?? "",
            newProduct: hasProductChanged ? { id: null, url: newProductUrl } : undefined,
            fromCache: true, // Flag to indicate this is from cache
          })
        );
      }

      // Still fetch HTML in background for morph (custom elements, etc.)
      requestIdleCallback(() => {
        this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, true);
      });

      if(this.quickAddDrawerContent && variantId){
      setTimeout(() => {
        this._handleInventoryCheck(variantId,this.productID)
      }, 200);    
      }

    } else if (variantId) {
      // CACHE MISS: Fetch lightweight JSON (faster than full HTML)
      // Detect if product changed (for combine listings in quick-add drawer, product cards, etc.)
      const newProductUrl = selectedOption.dataset.connectedProductUrl;
      const currentProductUrl = this.dataset.productUrl?.split("?")[0];
      const hasProductChanged = newProductUrl && currentProductUrl !== newProductUrl;

      this.#fetchVariantJson(variantId)
        .then((variantData) => {
          if (variantData) {
            // Update UI with fetched JSON data
            this.#updateCriticalUI(variantData, true);

            // Dispatch event immediately for other components
            if (this.selectedOptionId) {
              this.dispatchEvent(
                new VariantUpdateEvent(variantData, this.selectedOptionId, {
                  html: null, // No HTML yet
                  productId: this.dataset.productId ?? "",
                  newProduct: hasProductChanged ? { id: null, url: newProductUrl } : undefined,
                  fromCache: true, // Treated as cache hit for UI logic
                })
              );

              if(this.quickAddDrawerContent && variantId){
                setTimeout(() => {
                  this._handleInventoryCheck(variantId,this.productID)
                }, 200);    
              }

            }
          }
        })
        .catch((error) => {
          console.warn("Failed to fetch variant JSON:", error);
        });

      // Fetch full HTML in background for morph (custom elements, etc.)
      requestIdleCallback(() => {
        this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, true);
      });
    } else {
      // FALLBACK: No variant ID - fetch full HTML as fallback
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, false);
    }

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
    //      document.dispatchEvent(
    //   new CustomEvent("product:variant-change", {
    //     bubbles: true,
    //   })
    // );
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }

    // if (variantId) {
    //  if(event.target.closest("quick-add-dialog")){
    //  this.quickAddDrawerContent = document.getElementById("quick-add-drawer-content");
    //  }else{
    //   this.quickAddDrawerContent = this.closest(".product-information__details");
    //  } 
    // this.addToCartButton = this.quickAddDrawerContent.querySelector(".add-to-cart-button");
    // this.addToCartButtonText = this.addToCartButton.querySelector(".add-to-cart-text .add-to-cart-text__content");
    // var variantId = this.quickAddDrawerContent.querySelector("[name='id']").value;
    // var productID = this.querySelector("[name='product-id']").value;
    // this._handleInventoryCheck(variantId, productID);
    // }

    // if(this.quickAddDrawerContent && variantId){
    //   setTimeout(() => {
    //     this._handleInventoryCheck(variantId,this.productID)
    //   }, 100);    
    // }
    if(variantId && isOnProductPage){
      var variantMetaListBlock = document.querySelectorAll('.variant-meta-text-block');
      if (variantMetaListBlock.length) {
        variantMetaListBlock.forEach(function (item) {
          var blockLists = item.querySelectorAll('ul');
          if (blockLists.length) {
            blockLists.forEach(function (el) {
              if (el.getAttribute('data-id') == variantId) {
                el.classList.add('is_active');
              } else {
                el.classList.remove('is_active');
              }
            });
          }
        });
      }

      setTimeout(() => {
      this.updateGlowButton(variantId);
      this.updateYouTubeVideo(variantId); 
      }, 500);   
    } 

  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest("quick-add-dialog") || this.closest("swatches-variant-picker")) {
      let sectionId = this.quickAddDrawerId;
      if (this.closest("swatches-variant-picker")) {
        sectionId = "section-rendering-product-card";
      }
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${sectionId}&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {boolean} isBackgroundSync - If this is a background sync after cache hit (don't dispatch events again)
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, isBackgroundSync = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, "text/html");
        const variantData = this.#extractVariantData(html);

        if (variantData) {
          if (!isBackgroundSync) {
            // Update critical UI immediately (only if not from cache)
            // Note: Media is NOT updated here - will be replaced via HTML morph
            this.#updateCriticalUI(variantData, false);
          }
        } else {
          console.warn("No variant data extracted - dispatching without optimistic update");
        }

        // Morph HTML (will happen after critical UI update)
        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Always dispatch event with HTML (even without variantData or for background sync)
          // Components need HTML to morph elements (button text, inventory, badge)
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData || null, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
                fromCache: false, // Explicitly set to false for HTML fetch
                isBackgroundSync, // Flag to indicate this is background sync
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Fetch lightweight JSON data for a specific variant
   * This is faster than fetching full HTML (smaller payload)
   *
   * @param {string} variantId - The variant ID to fetch
   * @returns {Promise<Object|null>} The variant data or null
   */
  async #fetchVariantJson(variantId) {
    if (!variantId) return null;

    try {
      // Build URL to fetch variant-data section
      const productUrl = this.dataset.productUrl || window.location.pathname;
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("sections", "variant-data");
      url.searchParams.set("variant", variantId);

      // Abort previous JSON fetch if still pending (rapid clicking)
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      // Fetch section (Shopify wraps in HTML, not pure JSON)
      const response = await fetch(url.toString(), { signal: this.#abortController.signal });

      if (!response.ok) {
        console.warn(`Variant JSON fetch failed: ${response.status}`);
        return null;
      }

      // Section Rendering API returns: { "variant-data": "HTML string" }
      const data = await response.json();
      const htmlString = data["variant-data"];

      if (!htmlString) {
        console.warn("No variant-data in response");
        return null;
      }

      // Parse HTML to extract JSON from section content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlString;
      const sectionDiv = tempDiv.querySelector("#shopify-section-variant-data");

      if (!sectionDiv?.textContent) {
        console.warn("Could not extract JSON from section");
        return null;
      }

      // Parse the JSON from section content
      const variantData = JSON.parse(sectionDiv.textContent);

      return variantData;
    } catch (error) {
      // Silent abort errors (user clicked another variant)
      if (error.name === "AbortError") return null;

      console.error("Error fetching variant JSON:", error);
      return null;
    }
  }

  /**
   * Extract variant data from HTML response
   * @param {Document} html - The parsed HTML document
   * @returns {Object | null} The variant data or null if not found
   */
  #extractVariantData(html) {
    const scriptTag = html.querySelector('variant-picker script[type="application/json"]');

    if (!scriptTag?.textContent) {
      console.warn("No variant data found in response");
      return null;
    }

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error("Failed to parse variant data:", e);
      return null;
    }
  }

  /**
   * Update critical UI elements immediately with variant data
   * @param {Object} variantData - The variant object from JSON
   * @param {boolean} isFromCache - Whether this update is from cache (true) or from server (false)
   */
  #updateCriticalUI(variantData, isFromCache = false) {
    // 1. Update price immediately
    this.#updatePriceOptimistic(variantData);

    // 2. Update media gallery (only for cache, server fetch will replace entire gallery)
    if (isFromCache) {
      this.#updateMediaOptimistic(variantData);
    }

    // 3. Update button state
    this.#updateButtonStateOptimistic(variantData);
  }

  /**
   * Update price optimistically from variant data
   * @param {Object} variant - The variant object
   */
  #updatePriceOptimistic(variant) {
    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const priceComponent = scope?.querySelector("product-price");

    if (!priceComponent) return;

    // Dispatch optimistic update event
    priceComponent.dispatchEvent(
      new CustomEvent("price:update-optimistic", {
        detail: { variant },
        bubbles: false,
      })
    );
  }

  /**
   * Update media gallery optimistically
   * @param {Object} variant - The variant object
   */
  #updateMediaOptimistic(variant) {
    if (!variant.featured_media) return;

    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const mediaGallery = scope?.querySelector("media-gallery");

    if (!mediaGallery) return;

    // Dispatch optimistic media switch event
    mediaGallery.dispatchEvent(
      new CustomEvent("media:switch-optimistic", {
        detail: { variant },
        bubbles: false,
      })
    );
  }

  /**
   * Update button state optimistically
   * @param {Object} variant - The variant object
   */
  #updateButtonStateOptimistic(variant) {
    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const productForm = scope?.querySelector("product-form-component");

    if (!productForm) return;

    const addToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    if (!addToCartButton) return;

    // Update button state based on availability
    addToCartButton.disabled = !variant.available;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    if (!newHtml) return;

    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], .variant-option--fieldset input:checked");

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked")
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }

  get quickAddDrawerId() {
    const quickAddDrawer = document.getElementById("quick-add-drawer");
    return quickAddDrawer?.dataset.sectionId;
  }

  /**
   * HYBRID APPROACH: Load and cache preloaded variants for instant switching
   * Variants are preloaded in the initial HTML for zero-latency updates
   */
  #loadVariantsCache() {
    const cacheScript = this.querySelector("script[data-variants-cache]");

    if (!cacheScript?.textContent) {
      console.warn("⚠️ No variants cache found - will fetch from server");
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId found - cannot cache variants");
      return;
    }

    try {
      const data = JSON.parse(cacheScript.textContent);

      // Cache variants indexed by productId + option values for fast lookup
      if (data.variants && Array.isArray(data.variants)) {
        data.variants.forEach((variant) => {
          // Create cache key from productId + option values
          const key = this.#buildCacheKey(variant);
          globalVariantsCache.set(key, variant);
        });
      }

      // Cache media for quick reference (with productId prefix)
      if (data.media && Array.isArray(data.media)) {
        data.media.forEach((media) => {
          const mediaKey = `${productId}|${media.id}`;
          globalMediaCache.set(mediaKey, media);
        });
      }
    } catch (e) {
      console.error("Failed to load variants cache:", e);
    }
  }

  #buildCacheKey(variant) {
    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId for cache key");
      return "";
    }

    // Build key from productId + available options (option1, option2, option3)
    const parts = [productId];
    if (variant.option1) parts.push(variant.option1);
    if (variant.option2) parts.push(variant.option2);
    if (variant.option3) parts.push(variant.option3);
    return parts.join("|");
  }

  #getCachedVariant() {
    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId - cannot get cached variant");
      return null;
    }

    // Get currently selected option values (scoped to this variant-picker instance)
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked")
    );

    if (selectedOptions.length === 0) return null;

    // Build cache key from productId + selected values
    const optionValues = selectedOptions.map((el) => el.value).join("|");
    const key = `${productId}|${optionValues}`;

    return globalVariantsCache.get(key) || null;
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
